import {
  CloudSun,
  Radio,
  Database,
  Cpu,
  Sparkles,
  ShieldCheck,
  Volume2,
  Stars,
  Wind,
  Activity,
  Lock,
  ArrowRight,
} from "lucide-react";
import { GlassPanel } from "@/components/ui/glass-panel";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface SourceCard {
  name: string;
  agency: string;
  feeds: string;
  url: string;
  region: string;
  status: "primary" | "supplemental";
}

const SOURCES: SourceCard[] = [
  {
    name: "NOAA / National Weather Service",
    agency: "U.S. National Oceanic & Atmospheric Administration",
    feeds: "Surface observations · forecast guidance · severe-weather products",
    url: "https://www.weather.gov",
    region: "United States",
    status: "primary",
  },
  {
    name: "Environment & Climate Change Canada",
    agency: "Government of Canada",
    feeds: "Severe-weather warnings, watches, advisories · station observations",
    url: "https://weather.gc.ca",
    region: "Canada",
    status: "primary",
  },
  {
    name: "METAR Aviation Weather",
    agency: "International airport weather stations",
    feeds: "Real-time temperature, wind, pressure, visibility from thousands of stations",
    url: "https://aviationweather.gov",
    region: "Global · 9,000+ stations",
    status: "primary",
  },
  {
    name: "NOAA Space Weather Prediction Center",
    agency: "U.S. National Oceanic & Atmospheric Administration",
    feeds: "Planetary K-index forecast · auroral oval visibility",
    url: "https://www.swpc.noaa.gov",
    region: "Global · public-domain",
    status: "primary",
  },
  {
    name: "ECMWF ERA5 Reanalysis",
    agency: "European Centre for Medium-Range Weather Forecasts",
    feeds: "30 years of climate-grade daily highs, lows, and precipitation",
    url: "https://www.ecmwf.int/en/forecasts/dataset/ecmwf-reanalysis-v5",
    region: "Global · 1940 → present",
    status: "primary",
  },
  {
    name: "Copernicus Atmosphere Monitoring (CAMS)",
    agency: "European Union Copernicus programme",
    feeds: "Air quality (PM2.5, PM10, NO₂, O₃) · pollen counts",
    url: "https://atmosphere.copernicus.eu",
    region: "Global · pollen Europe-best",
    status: "supplemental",
  },
  {
    name: "National meteorological agencies",
    agency: "DWD (Germany) · Met Office (UK) · JMA (Japan) · Météo-France · BoM (Australia)",
    feeds: "Forecast guidance and observations harmonized into a single feed",
    url: "https://public.wmo.int",
    region: "Global · WMO members",
    status: "supplemental",
  },
];

interface MethodEntry {
  title: string;
  detail: string;
  reference: string;
}

const METHODS: MethodEntry[] = [
  {
    title: "Solar position",
    detail:
      "Equation-of-time correction, declination, and hour-angle conversion produce the sun's altitude and azimuth at your exact coordinates, refreshed every minute.",
    reference: "NOAA Solar Calculator",
  },
  {
    title: "Lunar position & phase",
    detail:
      "The moon's longitude, latitude, and distance are computed from established astronomical series. Phase angle drives the rendered terminator on the moon disk.",
    reference: "Astronomical Algorithms · Meeus",
  },
  {
    title: "Twilight phases",
    detail:
      "Solar altitude is sampled across the day and zero-crossings at −0.833°, −6°, −12°, and −18° pin down sunrise, civil, nautical, and astronomical twilight. Polar day and polar night are detected automatically.",
    reference: "International astronomy conventions",
  },
  {
    title: "Planet visibility",
    detail:
      "Mean orbital elements with century rates are advanced for Mercury through Saturn; Kepler's equation is solved per planet, and the geocentric position is transformed to your local sky. Approximate magnitudes come from heliocentric and geocentric distances.",
    reference: "JPL Solar System Dynamics",
  },
  {
    title: "Best outdoor window",
    detail:
      "Every 2- and 3-hour block in the next 18 hours is scored for thermal comfort (peak around 18 °C feels-like), wind tolerance, and precipitation probability. The highest-scoring block is shown as today's outdoor window.",
    reference: "Composite comfort score",
  },
  {
    title: "Comfort & driving safety",
    detail:
      "Comfort: a weighted blend of feels-like temperature, humidity, wind, UV, and air quality on a 0–100 scale. Driving risk: a composite of visibility, dew-point spread (an icing signal), precipitation rate, and gust strength with itemized factors.",
    reference: "EPA AQI bands · NWS road advisories",
  },
  {
    title: "Stargazing & sunset quality",
    detail:
      "Stargazing blends cloud cover, humidity, and moon illumination. Sunset quality favors a 30–70% cloud sweet-spot — mid clouds catch the color — with bonuses for dry air and clear visibility.",
    reference: "Dark-sky observation guidance",
  },
  {
    title: "Activity scoring",
    detail:
      "Per-activity ideal temperature bands, wind tolerance, and precipitation thresholds for running, cycling, walking, golf, and photography, with secondary penalties for poor air quality and high UV.",
    reference: "ACSM activity guidelines · Met Office outdoor guidance",
  },
];

export default function AboutPage() {
  return (
    <div className="space-y-6 animate-slide-up">
      <PageHeader
        eyebrow="About"
        title="The story behind the forecast"
        subtitle="Public agencies. Real science. None of your data sold."
      />

      {/* Hero */}
      <GlassPanel
        intensity="medium"
        className="relative overflow-hidden p-6 sm:p-8 border-white/10"
        style={{
          background:
            "linear-gradient(135deg, oklch(0.07 0.05 270) 0%, oklch(0.18 0.10 250) 50%, oklch(0.07 0.05 270) 100%)",
        }}
      >
        <div className="absolute inset-0 atmospheric-grid opacity-30 pointer-events-none" />
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full blur-3xl opacity-25 glow-pulse pointer-events-none"
          style={{ background: "oklch(0.6 0.2 240)" }} />
        <div className="relative">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-primary/90 font-semibold mb-3">
            <span className="h-2 w-2 rounded-full bg-emerald-400 glow-pulse" />
            Built for clarity, not engagement
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white max-w-3xl leading-tight">
            Your weather, decoded with science — sourced from the same observation networks national forecasters use.
          </h1>
          <p className="mt-4 text-sm sm:text-base text-white/80 leading-relaxed max-w-3xl">
            Vertali Weather pulls from the public meteorological infrastructure the world's national weather services
            already trust: airport METAR stations, NOAA and Environment Canada products, ECMWF reanalysis archives,
            and Copernicus atmospheric monitoring. Every forecast surface, every astronomy panel, and every spoken
            briefing is computed without sending your queries through a corporate voice or recommendation engine.
          </p>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { icon: Radio, label: "Sources", value: "Public agencies" },
              { icon: Volume2, label: "Voice", value: "Private" },
              { icon: Lock, label: "Tracking", value: "None" },
              { icon: Sparkles, label: "Personality", value: "All science" },
            ].map(({ icon: Icon, label, value }) => (
              <div
                key={label}
                className="rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm px-3 py-2.5"
              >
                <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider text-white/60 font-bold">
                  <Icon className="h-3 w-3 text-primary" />
                  {label}
                </div>
                <p className="text-sm font-bold text-white mt-0.5">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </GlassPanel>

      {/* Pillars */}
      <div className="grid gap-4 md:grid-cols-3">
        <GlassPanel intensity="low" className="p-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10 ring-1 ring-emerald-500/20 mb-3">
            <Database className="h-5 w-5 text-emerald-400" />
          </div>
          <h3 className="text-sm font-bold tracking-tight">Open public data</h3>
          <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
            Observations come straight from public weather agencies — NOAA, Environment Canada,
            the UK Met Office, Deutscher Wetterdienst, JMA, and others — together with METAR
            airport stations and ECMWF's ERA5 reanalysis. Aurora forecasts come direct from
            NOAA's Space Weather Prediction Center.
          </p>
        </GlassPanel>

        <GlassPanel intensity="low" className="p-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-500/10 ring-1 ring-violet-500/20 mb-3">
            <Volume2 className="h-5 w-5 text-violet-300" />
          </div>
          <h3 className="text-sm font-bold tracking-tight">A briefing meant for you</h3>
          <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
            Tap the briefing button and a natural voice reads today's outlook — what to wear,
            whether to drive carefully, when the weather is best for a walk, and whether the
            sunset will be worth watching. The briefing is generated in private. Your text isn't
            sent to a corporate voice assistant or used to train anyone else's model.
          </p>
        </GlassPanel>

        <GlassPanel intensity="low" className="p-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/10 ring-1 ring-amber-500/20 mb-3">
            <Cpu className="h-5 w-5 text-amber-400" />
          </div>
          <h3 className="text-sm font-bold tracking-tight">Computed, not branded</h3>
          <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
            Sun positions, twilight times, planet visibility, stargazing scores, comfort indices,
            and meteor-shower windows are calculated using established astronomy and climate
            science — not pulled from a black-box vendor. Every number on this site has a
            traceable origin in published research or a public weather agency.
          </p>
        </GlassPanel>
      </div>

      {/* Data sources */}
      <GlassPanel intensity="low" className="p-5 sm:p-6">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
            <Radio className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h2 className="text-base font-bold tracking-tight">Data sources</h2>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Where every number on this site comes from
            </p>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {SOURCES.map((s) => (
            <a
              key={s.name}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group rounded-xl border border-border/40 bg-background/30 p-4 hover:border-primary/40 hover:bg-background/50 transition-colors flex flex-col"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="text-sm font-bold leading-tight group-hover:text-primary transition-colors">
                  {s.name}
                </h3>
                <Badge variant={s.status === "primary" ? "info" : "outline"} size="sm">
                  {s.status}
                </Badge>
              </div>
              <p className="text-[11px] text-muted-foreground leading-snug">{s.agency}</p>
              <p className="text-xs text-foreground/80 mt-2 leading-relaxed">{s.feeds}</p>
              <div className="mt-3 flex items-center justify-between text-[10px]">
                <span className="text-muted-foreground">{s.region}</span>
                <span className="inline-flex items-center gap-1 text-primary font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                  Visit <ArrowRight className="h-2.5 w-2.5" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </GlassPanel>

      {/* Voice & privacy callout */}
      <GlassPanel intensity="low" className="p-5 sm:p-6 bg-gradient-to-br from-violet-500/5 via-transparent to-transparent border-violet-500/15">
        <div className="grid gap-5 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-500/10 ring-1 ring-violet-500/30">
                <ShieldCheck className="h-4.5 w-4.5 text-violet-300" />
              </div>
              <h2 className="text-base font-bold tracking-tight">How we treat your data</h2>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Most weather apps quietly route your activity through advertising graphs and corporate
              voice assistants. We took a different path. The forecast comes from public agencies and
              the briefing is spoken in private — nothing about you leaves our service.
            </p>
          </div>
          <div className="lg:col-span-2 grid gap-3 sm:grid-cols-2">
            <PrivacyRow
              icon={Volume2}
              label="Voice"
              value="Generated privately"
              detail="Your spoken briefing is produced inside our service. No corporate voice assistant, no transcript, no cloud round-trip."
            />
            <PrivacyRow
              icon={Lock}
              label="Tracking"
              value="Off by default"
              detail="No advertising trackers, no analytics SDKs, no fingerprinting scripts. The pages load with what's needed and nothing else."
            />
            <PrivacyRow
              icon={Sparkles}
              label="AI insights"
              value="Optional, opt-in"
              detail="Narrative summaries on the Insights page are generated only when you visit it. They use a single weather payload — not a profile of you."
            />
            <PrivacyRow
              icon={ShieldCheck}
              label="Account data"
              value="Your locations, your call"
              detail="Saved cities, units, and theme preferences live in your account. Nothing is sold, shared, or merged with third-party datasets."
            />
          </div>
        </div>
      </GlassPanel>

      {/* Methodology */}
      <GlassPanel intensity="low" className="p-5 sm:p-6">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
            <Activity className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h2 className="text-base font-bold tracking-tight">How the numbers are made</h2>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
              The science behind every score and sky position
            </p>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-2">
          {METHODS.map((m) => (
            <div
              key={m.title}
              className="rounded-xl border border-border/40 bg-background/30 p-4"
            >
              <div className="flex items-baseline justify-between gap-2 mb-1.5">
                <h3 className="text-sm font-bold tracking-tight">{m.title}</h3>
                <span className="text-[10px] font-mono text-primary/80 truncate max-w-[55%] text-right">
                  {m.reference}
                </span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{m.detail}</p>
            </div>
          ))}
        </div>
      </GlassPanel>

      {/* Pages tour */}
      <GlassPanel intensity="low" className="p-5 sm:p-6">
        <h2 className="text-base font-bold tracking-tight mb-1">What's in this app</h2>
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-5">
          A quick tour
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Tour
            icon={CloudSun}
            title="Dashboard"
            text="Atmospheric hero with comfort + sunset chips, today's range bar, 6 metric pills, hourly preview, sun-progress track. Plus stargazing, aurora, pollen, activity scores, and the voice briefing."
          />
          <Tour
            icon={Stars}
            title="Astronomy"
            text="Live sky dome (sun arc, moon with phase shading, visible planets, stars at night), twilight phases bar with all four altitudes, 7-day moon strip, 7-night stargazing forecast, and the annual meteor-shower calendar."
          />
          <Tour
            icon={Wind}
            title="Forecast & Map"
            text="Hourly chart, 8-day outlook, wind compass with gust readout, environmental trends, and an animated weather-tile map with temperature, clouds, precipitation, wind, and pressure layers."
          />
          <Tour
            icon={ShieldCheck}
            title="Alerts & Driving"
            text="Severity-sorted active alerts with time-to-expire and source attribution. A driving-safety card composes fog/ice/hydroplaning/wind risk into one actionable score."
          />
          <Tour
            icon={Sparkles}
            title="Insights"
            text="A forecast calendar heat-map, a 30-year climate-normals comparison (today vs every other May 23 since 1996), and AI-generated narrative cards."
          />
          <Tour
            icon={Activity}
            title="Clothing & Compare"
            text="Multi-day outfit recommendations driven by feels-like temperature, wind, and precipitation. Compare any two cities side-by-side with normalized metrics."
          />
        </div>
      </GlassPanel>

      {/* Acknowledgements */}
      <GlassPanel intensity="low" className="p-5 sm:p-6">
        <h2 className="text-base font-bold tracking-tight mb-1">Acknowledgements</h2>
        <p className="text-xs text-muted-foreground leading-relaxed mt-2">
          Forecasts and observations are provided by national meteorological agencies and
          public scientific bodies including NOAA, Environment & Climate Change Canada, the
          UK Met Office, Deutscher Wetterdienst, JMA, the European Centre for Medium-Range
          Weather Forecasts, and the Copernicus Atmosphere Monitoring Service. Aurora data
          is in the U.S. public domain. Map imagery is rendered under each provider's
          license terms.
        </p>
      </GlassPanel>

      <p className="text-center text-[11px] text-muted-foreground py-4">
        Vertali Weather · clear forecasts, calm conversation, no surveillance.
      </p>
    </div>
  );
}

function PrivacyRow({
  icon: Icon,
  label,
  value,
  detail,
}: {
  icon: typeof Volume2;
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <div className="rounded-xl border border-border/40 bg-background/30 p-3.5">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-3.5 w-3.5 text-violet-300" />
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
          {label}
        </span>
      </div>
      <p className="text-sm font-bold leading-tight">{value}</p>
      <p className="text-[11px] text-muted-foreground leading-snug mt-1">{detail}</p>
    </div>
  );
}

function Tour({
  icon: Icon,
  title,
  text,
}: {
  icon: typeof CloudSun;
  title: string;
  text: string;
}) {
  return (
    <div className="rounded-xl border border-border/40 bg-background/30 p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-bold">{title}</h3>
      </div>
      <p className="text-[11px] text-muted-foreground leading-relaxed">{text}</p>
    </div>
  );
}
